import { createTheme, loadTheme, Theme } from "@fluentui/react";

/**
 * The available theme names
 */
export type ThemeName = "light" | "dark" | "system" | "classic";

/**
 * Determines whether a value is a supported theme name.
 *
 * @param value - The value to check
 * @returns true when the value is a valid theme name
 */
export const isThemeName = (value: string | undefined): value is ThemeName => {
  return value === "light" || value === "dark" || value === "system" || value === "classic";
};

/**
 * Normalizes an arbitrary preference value to a supported theme name.
 *
 * @param value - The stored preference value
 * @returns a valid theme name
 */
export const toThemeName = (value: string | undefined): ThemeName => {
  return isThemeName(value) ? value : "classic";
};

/**
 * The original website theme palette that predates the theme picker.
 */
const classicPalette = {
  palette: {
    themePrimary: "#0078d4",
    themeLighterAlt: "#f3f9fd",
    themeLighter: "#d0e7f8",
    themeLight: "#a9d3f2",
    themeTertiary: "#5ca9e5",
    themeSecondary: "#1a86d9",
    themeDarkAlt: "#006cbe",
    themeDark: "#005ba1",
    themeDarker: "#004377",
    neutralLighterAlt: "#f8f8f8",
    neutralLighter: "#f4f4f4",
    neutralLight: "#eaeaea",
    neutralQuaternaryAlt: "#dadada",
    neutralQuaternary: "#d0d0d0",
    neutralTertiaryAlt: "#c8c8c8",
    neutralTertiary: "#c2c2c2",
    neutralSecondary: "#717171",
    neutralPrimaryAlt: "#484644",
    neutralPrimary: "#333333",
    neutralDark: "#272727",
    black: "#1d1d1d",
    white: "#ffffff",
    greenLight: "#b2ff91",
  },
};

/**
 * The light theme definition
 */
export const lightTheme: Theme = createTheme({
  palette: {
    ...classicPalette.palette,
  },
});

/**
 * The classic theme definition
 */
export const classicTheme: Theme = createTheme({
  ...classicPalette,
  semanticColors: {
    bodyBackground: "#dadada",
    bodyBackgroundChecked: "#dadada",
    bodyBackgroundHovered: "#f4f4f4",
    bodyText: classicPalette.palette.neutralPrimary,
    bodySubtext: classicPalette.palette.neutralSecondary,
    defaultStateBackground: classicPalette.palette.white,
    infoBackground: "#eef6fc",
    successBackground: "#edf7e9",
    warningBackground: "#fff6dd",
    errorBackground: "#fdecee",
    menuBackground: "#dadada",
    menuItemBackgroundHovered: "#cdcdcd",
    actionLink: classicPalette.palette.neutralPrimary,
    actionLinkHovered: classicPalette.palette.neutralDark,
    inputBackground: "#ffffff",
  },
});

/**
 * The dark theme definition
 */
export const darkTheme: Theme = createTheme({
  palette: {
    themePrimary: "#4ba0e1",
    themeLighterAlt: "#030709",
    themeLighter: "#0c1924",
    themeLight: "#172f44",
    themeTertiary: "#2d5e87",
    themeSecondary: "#428ac6",
    themeDarkAlt: "#5caae4",
    themeDark: "#75b8e9",
    themeDarker: "#9acbef",
    neutralLighterAlt: "#282828",
    neutralLighter: "#313131",
    neutralLight: "#3f3f3f",
    neutralQuaternaryAlt: "#484848",
    neutralQuaternary: "#4f4f4f",
    neutralTertiaryAlt: "#6d6d6d",
    neutralTertiary: "#c8c8c8",
    neutralSecondary: "#d0d0d0",
    neutralPrimaryAlt: "#dadada",
    neutralPrimary: "#e4e4e4",
    neutralDark: "#eaeaea",
    black: "#f8f8f8",
    white: "#1e1e1e",
    greenLight: "#4caf50",
  },
  semanticColors: {
    bodyBackground: "#1e1e1e",
    bodyBackgroundChecked: "#1e1e1e",
    bodyText: "#e4e4e4",
    bodySubtext: "#d0d0d0",
    inputBackground: "#2d2d2d",
    inputText: "#e4e4e4",
    inputBorder: "#4f4f4f",
    inputFocusBorderAlt: "#4ba0e1",
    menuBackground: "#2d2d2d",
    menuItemBackgroundHovered: "#3f3f3f",
    menuItemText: "#e4e4e4",
    disabledBackground: "#2d2d2d",
    disabledText: "#6d6d6d",
    cardShadow: "0 0 2px rgba(0, 0, 0, 0.4)",
    cardShadowHovered: "0 0 4px rgba(0, 0, 0, 0.6)",
  },
});

/**
 * Shared elevation and border styling tokens for a resolved theme.
 */
interface ThemeSurfaceStyles {
  /** The background color used for elevated surfaces. */
  cardBackground: string;
  /** The background color used for result-page controls. */
  controlBackground: string;
  /** The default border color used for elevated surfaces. */
  cardBorder: string;
  /** The hover border color used for elevated surfaces. */
  cardBorderHover: string;
  /** The default shadow used for elevated surfaces. */
  cardShadow: string;
  /** The hover shadow used for elevated surfaces. */
  cardShadowHover: string;
  /** The default border color used for selectable controls. */
  controlBorder: string;
  /** The hover border color used for selectable controls. */
  controlBorderHover: string;
  /** The default shadow used for selectable controls. */
  controlShadow: string;
  /** The hover shadow used for selectable controls. */
  controlShadowHover: string;
}

const lightSurfaceStyles: ThemeSurfaceStyles = {
  cardBackground: lightTheme.semanticColors.bodyBackground,
  controlBackground: lightTheme.semanticColors.bodyBackground,
  cardBorder: "#d9d9d9",
  cardBorderHover: "#bdbdbd",
  cardShadow: "0 6px 18px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08)",
  cardShadowHover: "0 10px 24px rgba(0, 0, 0, 0.18), 0 4px 10px rgba(0, 0, 0, 0.12)",
  controlBorder: "#d0d0d0",
  controlBorderHover: "#b3b3b3",
  controlShadow: "0 4px 12px rgba(0, 0, 0, 0.12), 0 1px 4px rgba(0, 0, 0, 0.08)",
  controlShadowHover: "0 8px 18px rgba(0, 0, 0, 0.16), 0 3px 8px rgba(0, 0, 0, 0.1)",
};

const classicSurfaceStyles: ThemeSurfaceStyles = {
  cardBackground: classicTheme.semanticColors.defaultStateBackground,
  controlBackground: classicTheme.semanticColors.defaultStateBackground,
  cardBorder: "#cfcfcf",
  cardBorderHover: "#aeb9c6",
  cardShadow: "0 7px 20px rgba(0, 0, 0, 0.16), 0 2px 8px rgba(0, 0, 0, 0.1)",
  cardShadowHover: "0 12px 28px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.12)",
  controlBorder: "#c2cbd6",
  controlBorderHover: "#8ea9c3",
  controlShadow: "0 5px 14px rgba(0, 0, 0, 0.14), 0 2px 5px rgba(0, 0, 0, 0.09)",
  controlShadowHover: "0 9px 20px rgba(0, 0, 0, 0.18), 0 3px 8px rgba(0, 0, 0, 0.11)",
};

const darkSurfaceStyles: ThemeSurfaceStyles = {
  cardBackground: "#2a2a2a",
  controlBackground: darkTheme.semanticColors.bodyBackground,
  cardBorder: "#3f3f3f",
  cardBorderHover: "#5a5a5a",
  cardShadow: "0 8px 24px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.35)",
  cardShadowHover: "0 12px 32px rgba(0, 0, 0, 0.55), 0 4px 12px rgba(0, 0, 0, 0.4)",
  controlBorder: "#4f4f4f",
  controlBorderHover: "#6d6d6d",
  controlShadow: "0 8px 24px rgba(0, 0, 0, 0.45), 0 2px 8px rgba(0, 0, 0, 0.35)",
  controlShadowHover: "0 12px 32px rgba(0, 0, 0, 0.55), 0 4px 12px rgba(0, 0, 0, 0.4)",
};

/**
 * Gets the shared surface styling for the resolved theme.
 *
 * @param resolvedThemeName - The resolved theme name
 * @returns the surface styling values
 */
const getThemeSurfaceStyles = (resolvedThemeName: "light" | "dark" | "classic"): ThemeSurfaceStyles => {
  if (resolvedThemeName === "dark") {
    return darkSurfaceStyles;
  }

  if (resolvedThemeName === "classic") {
    return classicSurfaceStyles;
  }

  return lightSurfaceStyles;
};

/**
 * Resolves a theme name to the effective theme preference,
 * taking system preference into account for "system" mode.
 *
 * @param themeName - The theme name ("light", "dark", "system", or "classic")
 * @returns the resolved theme name
 */
export const resolveEffectiveTheme = (themeName: ThemeName): "light" | "dark" | "classic" => {
  if (themeName === "system") {
    if (typeof window !== "undefined" && window.matchMedia) {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    }
    return "light"; // Default to light theme if window or matchMedia is unavailable
  }
  return themeName;
};

/**
 * Gets the Fluent UI theme object for the given theme name
 *
 * @param themeName - The theme name ("light", "dark", "system", or "classic")
 * @returns the Fluent UI theme
 */
export const getThemeForName = (themeName: ThemeName): Theme => {
  const resolvedThemeName = resolveEffectiveTheme(themeName);

  if (resolvedThemeName === "dark") {
    return darkTheme;
  }

  if (resolvedThemeName === "classic") {
    return classicTheme;
  }

  return lightTheme;
};

/**
 * Applies the specified theme globally. Updates Fluent UI's global theme
 * and the document body background color.
 *
 * @param themeName - The theme name to apply ("light", "dark", "system", or "classic")
 * @returns the applied Fluent UI theme
 */
export const applyTheme = (themeName: ThemeName): Theme => {
  const theme = getThemeForName(themeName);
  const resolvedThemeName = resolveEffectiveTheme(themeName);
  const surfaceStyles = getThemeSurfaceStyles(resolvedThemeName);
  loadTheme(theme);
  const root = document.documentElement;
  if (root) {
    root.setAttribute("data-theme", resolvedThemeName);
    root.style.setProperty("--rca-body-background", theme.semanticColors.bodyBackgroundChecked);
    root.style.setProperty("--rca-body-text", theme.semanticColors.bodyText);
    root.style.setProperty("--rca-link-color", theme.semanticColors.actionLink);
    root.style.setProperty("--rca-link-hover-color", theme.semanticColors.actionLinkHovered);
    root.style.setProperty("--rca-spinner-primary", theme.palette.themePrimary);
    root.style.setProperty("--rca-spinner-secondary", theme.palette.themeLight);
    root.style.setProperty("--rca-card-background", surfaceStyles.cardBackground);
    root.style.setProperty("--rca-control-background", surfaceStyles.controlBackground);
    root.style.setProperty("--rca-card-border", surfaceStyles.cardBorder);
    root.style.setProperty("--rca-card-border-hover", surfaceStyles.cardBorderHover);
    root.style.setProperty("--rca-card-shadow", surfaceStyles.cardShadow);
    root.style.setProperty("--rca-card-shadow-hover", surfaceStyles.cardShadowHover);
    root.style.setProperty("--rca-control-border", surfaceStyles.controlBorder);
    root.style.setProperty("--rca-control-border-hover", surfaceStyles.controlBorderHover);
    root.style.setProperty("--rca-control-shadow", surfaceStyles.controlShadow);
    root.style.setProperty("--rca-control-shadow-hover", surfaceStyles.controlShadowHover);
  }
  if (document.body) {
    document.body.style.backgroundColor = theme.semanticColors.bodyBackgroundChecked;
  }
  return theme;
};
